import { useCallback, useEffect, useMemo } from 'react';
import { OptimizelyDecision } from '@optimizely/react-sdk';
import { v4 as uuidv4 } from 'uuid';
import { identify as analyticsIdentify, track as analyticsTrack } from '../..';
import type { ExperimentData, TraitsProps } from '../../interfaces';
import { getDevice } from './genericTracking';

export const EXPERIMENT_VIEWED = 'Experiment Viewed';
export const UUID_LOCAL_STORAGE_KEY = 'FFF_gtb_uuid';

export const isExperimentRunning = (optimizelyDecision: OptimizelyDecision) => {
  const isFeatureFlagEnabled = optimizelyDecision.enabled;
  const regex = /^default-rollout-\d+-\d+$/;
  // default-rollout means the feature flag is on but no experiment running
  const isRunning = !!optimizelyDecision.ruleKey && !regex.test(optimizelyDecision.ruleKey);
  return isFeatureFlagEnabled && isRunning;
};

export const getUUIDfromLocalStorage = () => {
  const uuidFromLocalStorage = localStorage.getItem(UUID_LOCAL_STORAGE_KEY);
  if (!uuidFromLocalStorage) {
    localStorage.setItem(UUID_LOCAL_STORAGE_KEY, uuidv4());
  }
  return localStorage.getItem(UUID_LOCAL_STORAGE_KEY) || '';
};

export const identify = (experimentData: TraitsProps) => {
  analyticsIdentify({
    traits: experimentData,
    options: {
      Optimizely: {
        userId: getUUIDfromLocalStorage(),
      },
    },
  });
};

export const track = (name: string, experimentData: ExperimentData) => {
  analyticsTrack({
    trackName: name,
    properties: experimentData,
    options: {
      Optimizely: {
        userId: getUUIDfromLocalStorage(),
      },
    },
  });
};

export const useTrackOptimizely = (
  decision: OptimizelyDecision,
  experimentName: string,
  enableTracking: boolean = true,
) => {
  const device = getDevice();
  const experimentData = useMemo(
    () => ({
      experiment_name: experimentName,
      variation_name: decision.variationKey,
      feature_flag_name: decision.flagKey,
      device,
    }),
    [experimentName, decision?.variationKey, decision?.flagKey],
  ) as ExperimentData;

  const isRunning = isExperimentRunning(decision);
  const traitsProps = {
    experimentName,
    variationName: decision.variationKey,
    featureFlagName: decision.flagKey,
  } as TraitsProps;

  useEffect(() => {
    if (isRunning && enableTracking) {
      track(EXPERIMENT_VIEWED, experimentData);
      identify(traitsProps);
    }
  }, [experimentData, isRunning, enableTracking]);

  // this method is used to trigger tracking on specific CTA
  const optimizelyTrack = useCallback(
    (eventName: string, trackingData: ExperimentData) => {
      if (decision && isExperimentRunning(decision)) {
        track(eventName, {
          ...experimentData,
          ...trackingData,
          device: device || '',
        });
      }
    },
    [decision],
  );

  // return a method to trigger tracking on specific CTA
  return { optimizelyTrack };
};
